import { useEffect, useState } from 'react';
import { AffilmasterApiError, getAffilmasterBankaUcty, type BankaUcet } from '../api/affilmaster';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { fmtDen } from './affilmaster-format';
import './affilmaster-banka.css';

type BankaTab = 'ucty';
const BANKA_TABS: readonly BankaTab[] = ['ucty'];

const CURRENCY_SYMBOL: Record<string, string> = { CZK: 'Kč', EUR: '€' };

interface CurrencySummary {
  currency: string;
  total: number;
  accountCount: number;
  balanceCount: number;
}

function fmtZustatek(v: number | null, currency: string): string {
  if (v == null) return 'Bez dat';
  const n = v.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n} ${CURRENCY_SYMBOL[currency] ?? currency}`;
}

function fmtCas(iso: string | null): string {
  if (!iso) return 'zatím neproběhla';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function pocetUctu(count: number): string {
  if (count === 1) return '1 účet';
  if (count >= 2 && count <= 4) return `${count} účty`;
  return `${count} účtů`;
}

function latestValue(values: Array<string | null>): string | null {
  return values.filter((value): value is string => Boolean(value)).sort().at(-1) ?? null;
}

function summarizeCurrencies(accounts: BankaUcet[]): CurrencySummary[] {
  const totals = new Map<string, CurrencySummary>();
  accounts.forEach((account) => {
    const current = totals.get(account.currency) ?? {
      currency: account.currency,
      total: 0,
      accountCount: 0,
      balanceCount: 0,
    };
    current.accountCount += 1;
    if (account.closingBalance != null) {
      current.total += account.closingBalance;
      current.balanceCount += 1;
    }
    totals.set(account.currency, current);
  });
  return Array.from(totals.values()).sort((a, b) => {
    if (a.currency === 'CZK') return -1;
    if (b.currency === 'CZK') return 1;
    return a.currency.localeCompare(b.currency, 'cs');
  });
}

export function AffilmasterBankaPage() {
  const [accounts, setAccounts] = useState<BankaUcet[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useTabParam<BankaTab>('tab', BANKA_TABS, 'ucty');

  useEffect(() => {
    let cancelled = false;
    getAffilmasterBankaUcty()
      .then((rows) => { if (!cancelled) { setAccounts(rows); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení účtů selhalo'); });
    return () => { cancelled = true; };
  }, []);

  useFeather([accounts, tab]);

  const balanceDate = accounts ? latestValue(accounts.map((account) => account.balanceDate)) : null;

  return (
    <section className="am-banka">
      <header className="prototype-page-head am-banka-page-head">
        <div>
          <div className="prototype-eyebrow">AFFILMASTER</div>
          <h1>Banka</h1>
          <p>Bankovní účty a aktuální zůstatky</p>
        </div>
        {accounts && accounts.length > 0 && (
          <div className="am-banka-head-meta" aria-label="Kontext bankovních dat">
            <span><i data-feather="credit-card" />{pocetUctu(accounts.length)}</span>
            <span>Data k {balanceDate ? fmtDen(balanceDate) : '—'}</span>
          </div>
        )}
      </header>
      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Bankovní účty se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !accounts && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}

      {accounts && (
        <>
          <div className="prototype-overview-toolbar">
            <div className="prototype-overview-tabs" role="tablist">
              <button type="button" className={tab === 'ucty' ? 'active' : ''} role="tab" onClick={() => setTab('ucty')}>Účty</button>
            </div>
          </div>
          {tab === 'ucty' && <TabUcty accounts={accounts} />}
        </>
      )}
    </section>
  );
}

function TabUcty({ accounts }: { accounts: BankaUcet[] }) {
  if (accounts.length === 0) {
    return <div className="prototype-panel am-banka-empty">Žádné bankovní účty nejsou nastavené.</div>;
  }

  const summaries = summarizeCurrencies(accounts);
  const syncOkCount = accounts.filter((account) => account.lastStatus === 'ok').length;
  const latestSync = latestValue(accounts.map((account) => account.lastSyncAt));

  return (
    <div className="am-banka-content">
      <article className="prototype-panel am-banka-summary" aria-label="Souhrn bankovních účtů">
        <div className="am-banka-summary-lead">
          <span>CELKEM PODLE MĚNY</span>
          <h2>Aktuální zůstatky</h2>
          <p>Každá měna je vedená samostatně, bez orientačního přepočtu.</p>
        </div>
        <div className="am-banka-currency-totals">
          {summaries.map((summary) => (
            <div className="am-banka-currency-total" key={summary.currency}>
              <span>{summary.currency}</span>
              <strong>{fmtZustatek(summary.balanceCount > 0 ? summary.total : null, summary.currency)}</strong>
              <small>{pocetUctu(summary.accountCount)}</small>
            </div>
          ))}
        </div>
        <div className={`am-banka-sync-summary${syncOkCount === accounts.length ? '' : ' is-warning'}`}>
          <span>Synchronizace</span>
          <strong>{syncOkCount} / {accounts.length}</strong>
          <small>{latestSync ? `Naposledy ${fmtCas(latestSync)}` : 'Zatím neproběhla'}</small>
        </div>
      </article>

      <section className="am-banka-section" aria-labelledby="am-banka-accounts-title">
        <div className="am-banka-section-head">
          <div><span>ÚČTY</span><h2 id="am-banka-accounts-title">Bankovní účty</h2></div>
          <p>{pocetUctu(accounts.length)}</p>
        </div>
        <div className="prototype-panel am-banka-accounts" role="list" aria-label="Bankovní účty">
          {accounts.map((account) => <UcetRow key={account.id} account={account} />)}
        </div>
      </section>
    </div>
  );
}

function UcetRow({ account }: { account: BankaUcet }) {
  const syncOk = account.lastStatus === 'ok';
  const cislo = account.accountNo ? `${account.accountNo}/${account.bankCode ?? ''}` : 'Doplní synchronizace';
  return (
    <article className={`am-banka-account${syncOk ? '' : ' is-error'}`} role="listitem">
      <div className="am-banka-account-identity">
        <span className="am-banka-account-icon"><i data-feather="credit-card" /></span>
        <div><strong>{account.name}</strong><span>{account.currency}</span></div>
      </div>
      <div className="am-banka-account-balance">
        <span>Zůstatek</span>
        <strong>{fmtZustatek(account.closingBalance, account.currency)}</strong>
        <small>{account.balanceDate ? `k ${fmtDen(account.balanceDate)}` : 'datum není dostupné'}</small>
      </div>
      <dl className="am-banka-account-details">
        <div><dt>Číslo účtu</dt><dd>{cislo}</dd></div>
        <div><dt>IBAN</dt><dd>{account.iban ?? 'Doplní synchronizace'}</dd></div>
      </dl>
      <div className="am-banka-account-sync">
        <span><i data-feather={syncOk ? 'check' : 'alert-circle'} />{syncOk ? 'Synchronizováno' : 'Vyžaduje kontrolu'}</span>
        <strong>{syncOk ? fmtCas(account.lastSyncAt) : (account.lastStatus ?? 'zatím neproběhla')}</strong>
      </div>
    </article>
  );
}
